/* eslint-disable no-param-reassign */
import { createAction } from '@reduxjs/toolkit';

import { LISTING_STATUS } from './constants/listingStatus';
import { findListingEntity, findListingVariantEntity } from './helpers/entityReadHelpers';
import { upsertListing, upsertListingVariant } from './helpers/entityAdapter';

const normalizeListingStatus = (value) => {
  const normalized = String(value || '').trim().toUpperCase();
  if (normalized === LISTING_STATUS.PUBLISHED || normalized === 'LIVE') {
    return LISTING_STATUS.PUBLISHED;
  }
  if (normalized === LISTING_STATUS.DRAFT) return LISTING_STATUS.DRAFT;
  return null;
};

export const buildListingSeedFromSelection = ({
  entry,
  searchHit,
  channelId,
  masterProductId,
  masterVariantId,
} = {}) => {
  if (!entry || !channelId || !masterProductId) return null;
  if (entry.type !== 'listing' && entry.type !== 'listingVariant') return null;

  const listingProductId = entry.type === 'listing'
    ? entry._id
    : (entry.baseProductId || searchHit?.baseProductId || searchHit?.baseProduct?._id);
  if (!listingProductId) return null;

  const listingStatus = normalizeListingStatus(
    entry.listingStatus || searchHit?.listingStatus,
  );
  const productLive = entry.type === 'listing'
    ? (entry.live || searchHit?.live)
    : (searchHit?.baseProduct?.live);
  const variantLive = entry.live || searchHit?.live;
  const resolvedVariantId = masterVariantId || entry.masterVariantId || searchHit?.masterVariantId;
  const listingDraftDomains = entry.type === 'listing'
    ? (entry.draftDomains || searchHit?.draftDomains)
    : searchHit?.baseProduct?.draftDomains;
  const variantDraftDomains = entry.type === 'listingVariant'
    ? (entry.draftDomains || searchHit?.draftDomains)
    : null;

  const listing = {
    _id: listingProductId,
    masterProductId,
    salesChannelId: String(channelId),
    ...(listingStatus ? { listingStatus } : {}),
    ...(productLive ? { live: productLive } : {}),
    ...(listingDraftDomains ? { draftDomains: listingDraftDomains } : {}),
  };

  if (entry.type !== 'listingVariant' || !resolvedVariantId) {
    return { listing, listingVariant: null };
  }

  return {
    listing,
    listingVariant: {
      _id: entry._id,
      masterProductId,
      masterVariantId: resolvedVariantId,
      baseProductId: listingProductId,
      salesChannelId: String(channelId),
      ...(listingStatus ? { listingStatus } : {}),
      ...(variantLive ? { live: variantLive } : {}),
      ...(variantDraftDomains ? { draftDomains: variantDraftDomains } : {}),
    },
  };
};

export const seedListingEntitiesFromSelection = createAction(
  'products/seedListingEntitiesFromSelection',
);

export const reducer = (builder) => {
  builder.addCase(seedListingEntitiesFromSelection, (state, { payload }) => {
    const seed = buildListingSeedFromSelection(payload);
    if (!seed) return;

    const channelId = payload.channelId;
    const masterProductId = payload.masterProductId;

    if (!findListingEntity(state, { masterProductId, channelId })) {
      upsertListing(state, seed.listing);
    }

    if (
      seed.listingVariant
      && !findListingVariantEntity(state, seed.listingVariant.masterVariantId, channelId)
    ) {
      upsertListingVariant(state, seed.listingVariant);
    }
  });
};
